import React from 'react';
import styled from '@emotion/styled';
import { MuiButton } from '@onehope/design-system-ohw';
import theme from '../../../../styles/themes/hopeTheme';
import NumberOne from './number-one';
import NumberTwo from './number-two';

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  max-width: 1205px;
  font-size: 18px;
  line-height: 1.64;
  font-family: ${theme.headerBold};
  color: #373634;
  background-color: #f4f2ef;
  @media screen and (min-width: 768px) {
    margin: 0 40px;
  }
  @media screen and (min-width: 1440px) {
    flex-direction: row;
    font-size: 14px;
    line-height: 1.57;
    margin: 80px auto;
  }

  ul {
    padding: 0 0 0 16px;
    margin: 0;
  }

  li {
    margin-bottom: 4px;
  }

  li:before {
    content: '';
    display: inline-block;
    height: 1rem;
    margin-left: -6px;
  }
`;

const LeftContainer = styled.div`
  width: 288px;
  margin: 64px;
  text-align: center;
  @media screen and (min-width: 768px) {
    width: 460px;
    margin: 64px 0 32px 0;
  }
  @media screen and (min-width: 1440px) {
    margin: 64px;
    width: 560px;
    text-align: left;
  }
`;

const RightContainer = styled.div`
  width: 288px;
  padding: 40px 16px;
  @media screen and (min-width: 768px) {
    width: 472px;
    padding: 32px 0 64px 0;
  }
  @media screen and (min-width: 1440px) {
    width: 645px;
    padding: 64px 80px 64px 0;
  }
`;

const ListItem = styled.div`
  display: flex;
`;

const Padding = styled.div`
  padding-bottom: 40px;
`;

const Title = styled.div`
  font-family: 'Playfair Display', 'proxima-nova', serif;
  font-size: 44px;
  line-height: 60px;
  /* or 136% */
  letter-spacing: 0.03em;
  max-width: 588px;
  width: 100%;
  margin: 0 auto;
  @media screen and (min-width: 1440px) {
    max-width: 440px;
    margin: 0;
  }
`;

const Summary = styled.div`
  padding: 32px 0;
  max-width: 533px;
  width: 100%;
  margin: 0 auto;
  @media screen and (min-width: 1440px) {
    margin: 0;
  }
`;

const SubTitle = styled.div`
  text-transform: uppercase;
  font-weight: 500;
  padding-bottom: 8px;
  margin: 0 auto;
`;

const Button = styled(MuiButton)`
  && {
    max-width: 288px;
  }
`;

const IconContainer = styled.div`
  width: 40px;
  padding: 8px 24px 0 0;
  @media screen and (min-width: 1440px) {
    padding: 0 24px;
  }
`;

function createMarkup(summary: string) {
  return { __html: summary };
}

interface FeatureHighlightProps {
  config: {
    title?: string;
    summary?: string;
    buttonLink?: string;
    buttonText?: string;
    featureTitleOne?: string;
    featureTitleTwo?: string;
    featureTextOne?: string;
    featureTextTwo?: string;
  };
  handleRouteChange: (input: string) => void;
}

export default function FeatureHighlight({
  config,
  handleRouteChange,
}: FeatureHighlightProps) {
  const {
    title,
    summary,
    buttonLink,
    buttonText,
    featureTitleOne,
    featureTitleTwo,
    featureTextOne,
    featureTextTwo,
  } = config;

  const onClick = () => {
    if (buttonLink) {
      handleRouteChange(buttonLink);
    }
  };

  return (
    <Container>
      <LeftContainer>
        <Title>{title}</Title>
        <Summary>{summary}</Summary>
        <Button onClick={onClick} type="PRIMARY" fullWidth>
          {buttonText}
        </Button>
      </LeftContainer>
      <RightContainer>
        <ListItem>
          <IconContainer>
            <NumberOne />
          </IconContainer>
          <div>
            <SubTitle>{featureTitleOne}</SubTitle>
            {featureTextOne && (
              <div dangerouslySetInnerHTML={createMarkup(featureTextOne)} />
            )}
          </div>
        </ListItem>
        <Padding />
        <ListItem>
          <IconContainer>
            <NumberTwo />
          </IconContainer>
          <div>
            <SubTitle>{featureTitleTwo}</SubTitle>
            {featureTextTwo && (
              <div dangerouslySetInnerHTML={createMarkup(featureTextTwo)} />
            )}
          </div>
        </ListItem>
      </RightContainer>
    </Container>
  );
}
